import { useState, useEffect, useCallback, useRef } from 'react';
import { TagPickerItem, SearchFunction, PrepopulateFunction } from './GenericTagPicker.types';

export interface UseTagPickerSearchProps<T extends TagPickerItem, TContext = unknown> {
    searchFunction?: SearchFunction<T, TContext>;
    prepopulateFunction?: PrepopulateFunction<T, TContext>;
    searchContext?: TContext;
    minSearchLength?: number;
    searchDebounceMs?: number;
    enableWildcardSearch?: boolean; // New: Enable '%' wildcard search behavior
}

export interface UseTagPickerSearchResult<T extends TagPickerItem> {
    items: T[];
    isLoadingItems: boolean;
    isSearching: boolean;
    query: string;
    setQuery: (query: string) => void;
    isDropdownOpen: boolean;
    setIsDropdownOpen: (open: boolean) => void;
    hasCalledInitialFocus: boolean;
    setHasCalledInitialFocus: (called: boolean) => void;
    performSearch: (searchTerm: string) => Promise<void>;
}

export const useTagPickerSearch = <T extends TagPickerItem, TContext = unknown>({
    searchFunction,
    prepopulateFunction,
    searchContext,
    minSearchLength = 3,
    searchDebounceMs = 300,
    enableWildcardSearch = true // Default to enabled for backward compatibility
}: UseTagPickerSearchProps<T, TContext>): UseTagPickerSearchResult<T> => {
    const [items, setItems] = useState<T[]>([]);
    const [allCachedItems, setAllCachedItems] = useState<T[]>([]); // Cache for local filtering
    const [isLoadingItems, setIsLoadingItems] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [query, setQuery] = useState<string>('');
    const [hasCalledInitialFocus, setHasCalledInitialFocus] = useState(false);
    const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchedTerms, setSearchedTerms] = useState<Set<string>>(new Set()); // Track server searches
    const lastSearchContextRef = useRef<TContext | undefined>(searchContext);

    // Local filtering function - smart filtering with word boundary matching
    const filterLocalItems = useCallback((searchTerm: string, itemsToFilter: T[]): T[] => {
        // Handle special wildcard case - '%' should show all items like empty search (if enabled)
        if (!searchTerm || (enableWildcardSearch && searchTerm === '%')) {
            return itemsToFilter.slice(0, 10); // Show first 10 when no search or wildcard
        }

        const lowerSearchTerm = searchTerm.toLowerCase();

        // Score-based filtering for better relevance
        const scoredItems = itemsToFilter.map(item => {
            const lowerDisplayText = item.displayText.toLowerCase();
            let score = 0;

            // Exact match gets highest score
            if (lowerDisplayText === lowerSearchTerm) score = 1000;
            // Starts with search term gets high score
            else if (lowerDisplayText.startsWith(lowerSearchTerm)) score = 100;
            // Contains search term gets medium score
            else if (lowerDisplayText.includes(lowerSearchTerm)) score = 10;
            // No match gets 0 (already initialized)

            return { item, score };
        });

        // Filter out non-matches and sort by score, then alphabetically
        const filtered = scoredItems
            .filter(({ score }) => score > 0)
            .sort((a, b) => {
                if (b.score !== a.score) return b.score - a.score;
                return a.item.displayText.localeCompare(b.item.displayText);
            })
            .map(({ item }) => item);

        return filtered.slice(0, 10); // Always limit to 10 results
    }, [enableWildcardSearch]);

    const performSearch = useCallback(async (searchTerm: string) => {
        if (!searchFunction) return;

        // Normalize search term for comparison
        const normalizedSearchTerm = (searchTerm || "*").toLowerCase();

        // Special handling for '*' wildcard search - always try local first, but allow server call
        const isWildcardSearch = enableWildcardSearch && (normalizedSearchTerm === "*" || normalizedSearchTerm === "%");

        // First, try local filtering from cached items
        const localResults = filterLocalItems(searchTerm, allCachedItems);

        // For wildcard searches, if we have cached items, show them first
        // For regular searches, if we have local results or search term is too short, use local results
        if (localResults.length > 0 && !isWildcardSearch) {
            setItems(localResults);
            return;
        } else if (localResults.length > 0 && isWildcardSearch) {
            // For wildcard, show local results but still allow server call if not searched yet
            setItems(localResults);
        }

        // Check minimum length requirement (skip for wildcard searches)
        if (!isWildcardSearch && searchTerm.length < minSearchLength) {
            setItems(localResults);
            return;
        }

        // Check if we've already searched for this term on the server
        if (searchedTerms.has(normalizedSearchTerm)) {
            // We already searched for this term, don't call server again
            if (!isWildcardSearch || localResults.length === 0) {
                setItems([]);
            }
            return;
        }

        // No local results found and haven't searched this term - call server
        setIsSearching(true);
        try {
            // Don't double-convert: if searchTerm is already "*" or "%", use it as-is
            const finalSearchTerm = searchTerm || "*";
            const result = await searchFunction(finalSearchTerm, searchContext);

            // Mark this search term as completed
            setSearchedTerms(prev => new Set([...prev, normalizedSearchTerm]));

            // Cache all results for future local filtering
            setAllCachedItems(prevCached => {
                const existingIds = new Set(prevCached.map(item => item.id));
                const newItems = result.filter(newItem => !existingIds.has(newItem.id));
                return [...prevCached, ...newItems];
            });

            // Show filtered results (limited to 10)
            const filteredResults = filterLocalItems(searchTerm, result);
            setItems(filteredResults);

            // For better dropdown behavior after search, ensure dropdown is open if we have results
            if (filteredResults.length > 0) {
                setTimeout(() => setIsDropdownOpen(true), 50);
            }
        } catch (err: unknown) {
            // Error handling for search failure
            if (err instanceof Error) {
                // Could log error here if needed
            }
            setItems([]);
        } finally {
            setIsSearching(false);
        }
    }, [searchFunction, searchContext, filterLocalItems, allCachedItems, minSearchLength, searchedTerms, setIsDropdownOpen, enableWildcardSearch]);

    // Debounced search function - stable reference
    const debouncedSearchRef = useRef<(searchTerm: string) => void>();

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        debouncedSearchRef.current = (searchTerm: string) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                void performSearch(searchTerm);
            }, searchDebounceMs);
        };

        return () => clearTimeout(timeoutId);
    }, [performSearch, searchDebounceMs]);    // Load initial prepopulated data - trigger only on mount and context changes
    useEffect(() => {
        // Check if context actually changed
        const contextChanged = JSON.stringify(lastSearchContextRef.current) !== JSON.stringify(searchContext);

        if (!prepopulateFunction) {
            setItems([]);
            return;
        }

        // If context hasn't changed and we've already loaded, don't reload
        if (!contextChanged && hasLoadedInitialData) {
            return;
        }

        // Update the ref to track this context
        lastSearchContextRef.current = searchContext;
        setHasLoadedInitialData(false); // Reset for new context
        setQuery(''); // Clear search query for new context
        setIsSearching(false); // Reset searching state
        setSearchedTerms(new Set()); // Clear searched terms for new context

        let isCancelled = false;

        const loadData = async () => {
            setIsLoadingItems(true);
            try {
                const prepopulatedResult = await prepopulateFunction(searchContext);
                if (!isCancelled) {
                    // Cache the prepopulated results
                    setAllCachedItems(prepopulatedResult);
                    // Show only first 10 results initially
                    setItems(prepopulatedResult.slice(0, 10));
                    setHasLoadedInitialData(true);
                }
            } catch (err: unknown) {
                if (!isCancelled) {
                    // Error handling for prepopulate failure
                    if (err instanceof Error) {
                        // Could log error here if needed
                    }
                    setItems([]); // Set empty on error
                    setAllCachedItems([]); // Clear cache on error
                    setHasLoadedInitialData(true); // Still mark as loaded to prevent retry
                }
            } finally {
                if (!isCancelled) {
                    setIsLoadingItems(false);
                }
            }
        };

        void loadData();

        return () => {
            isCancelled = true;
        };
    }, [searchContext, prepopulateFunction, hasLoadedInitialData]);

    // Handle query changes through useEffect when query state changes
    useEffect(() => {
        // Don't search if no search function provided
        if (!searchFunction) {
            return;
        }

        // Special case: if user types "%", treat it as "*" for immediate search (if enabled)
        // But still use smart search behavior (10 results, caching, etc.)
        if (enableWildcardSearch && query === '%') {
            // First show cached results immediately if available
            const immediateResults = filterLocalItems('%', allCachedItems);
            setItems(immediateResults);

            // Then perform search to get more comprehensive results
            void performSearch('*');
            return;
        }

        // For any query length, try local filtering first
        const localResults = filterLocalItems(query, allCachedItems);
        setItems(localResults);

        // If we have local results or query is too short, don't call server
        if (localResults.length > 0 || query.length < minSearchLength) {
            return;
        }

        // Check if we've already searched for this exact term
        const normalizedQuery = query.toLowerCase();
        if (searchedTerms.has(normalizedQuery)) {
            // Already searched this term, no need to call server again
            return;
        }

        // No local results, query is long enough, and haven't searched this term - call server
        debouncedSearchRef.current?.(query);
    }, [query, searchFunction, minSearchLength, filterLocalItems, allCachedItems, searchedTerms, enableWildcardSearch, performSearch]);

    return {
        items,
        isLoadingItems,
        isSearching,
        query,
        setQuery,
        isDropdownOpen,
        setIsDropdownOpen,
        hasCalledInitialFocus,
        setHasCalledInitialFocus,
        performSearch
    };
};
